import { useRef } from 'react';
import moment from 'moment';
import { IPromptHistoryEntry } from '../types';

interface VersionHistoryProps {
    versions: IPromptHistoryEntry[];
    currentVersion?: number;
    currentVersionData?: IPromptHistoryEntry | null;
    loading?: boolean;
    loadingMore?: boolean;
    hasMore?: boolean;
    canRestore?: boolean;
    restoringVersion?: number | null;
    onRestore?: (version: number) => void;
    onViewVersion?: (version: number) => void;
    onLoadMore?: () => void;
}

const formatChangeType = (changeType?: string) => {
    if (!changeType) return null;
    return changeType
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getUpdatedByLabel = (updatedBy: IPromptHistoryEntry['updatedBy']) => {
    if (!updatedBy) return undefined;
    if (typeof updatedBy === 'string') return updatedBy;
    return updatedBy.username;
};

const getChangedByLabel = (entry: IPromptHistoryEntry) =>
    entry.changedBy?.name ||
    entry.changedBy?.username ||
    entry.changedBy?.email ||
    entry.createdBy ||
    getUpdatedByLabel(entry.updatedBy);

const VersionEntryDetails = ({ entry }: { entry: IPromptHistoryEntry }) => {
    const timestamp = entry.timestamp || entry.createdAt || entry.updatedAt;
    const changedBy = getChangedByLabel(entry);
    const changeType = formatChangeType(entry.changeType);

    return (
        <div className='space-y-1'>
            {changeType && (
                <p className='text-xs text-gray-700 dark:text-gray-300'>Change: {changeType}</p>
            )}
            {timestamp && (
                <p className='text-xs text-gray-500 dark:text-gray-400'>
                    {moment(timestamp).format('DD MMM YYYY, HH:mm')}
                    {changedBy ? ` · ${changedBy}` : ''}
                </p>
            )}
            {entry.restoredFromVersion != null && (
                <p className='text-xs text-amber-700 dark:text-amber-400'>
                    Restored from v{entry.restoredFromVersion}
                </p>
            )}
            {(entry.allowedProviders?.length || entry.allowedModels?.length) ? (
                <p className='text-xs text-gray-500 dark:text-gray-400'>
                    {(entry.allowedProviders || []).join(', ') || '—'}
                    {' · '}
                    {(entry.allowedModels || []).join(', ') || '—'}
                </p>
            ) : null}
            {entry.prompt && (
                <p className='text-xs text-gray-500 dark:text-gray-400 line-clamp-2 font-mono'>
                    {entry.prompt}
                </p>
            )}
        </div>
    );
};

const VersionHistory = ({
    versions,
    currentVersion,
    currentVersionData,
    loading,
    loadingMore,
    hasMore,
    canRestore,
    restoringVersion,
    onRestore,
    onViewVersion,
    onLoadMore,
}: VersionHistoryProps) => {
    const listRef = useRef<HTMLUListElement>(null);
    const sorted = [...versions]
        .filter((e) => currentVersion == null || e.version !== currentVersion)
        .sort((a, b) => (b.version ?? 0) - (a.version ?? 0));

    const currentEntry: IPromptHistoryEntry | null =
        currentVersionData ??
        (currentVersion != null
            ? versions.find((e) => e.version === currentVersion) ?? null
            : null);

    const handleScroll = () => {
        const el = listRef.current;
        if (!el || !onLoadMore || !hasMore || loading || loadingMore) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
            onLoadMore();
        }
    };

    return (
        <div className='border border-gray-200 dark:border-borderColor-darkMode rounded-xl overflow-hidden'>
            <div className='px-4 py-3 border-b border-gray-100 dark:border-borderColor-darkMode'>
                <h3 className='text-sm font-semibold text-gray-900 dark:text-white'>Version history</h3>
                {currentVersion != null && currentEntry && (
                    <div className='mt-3 space-y-1'>
                        <p className='text-sm font-medium text-gray-900 dark:text-white'>
                            v{currentVersion}
                            <span className='ml-2 text-xs font-normal text-green-700 dark:text-green-400'>
                                (current)
                            </span>
                        </p>
                        <VersionEntryDetails entry={currentEntry} />
                    </div>
                )}
                {currentVersion != null && !currentEntry && (
                    <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
                        Current version: v{currentVersion}
                    </p>
                )}
            </div>
            {loading ? (
                <p className='px-4 py-6 text-sm text-gray-500 dark:text-gray-400'>Loading history...</p>
            ) : sorted.length === 0 ? (
                <p className='px-4 py-6 text-sm text-gray-500 dark:text-gray-400'>No version history yet</p>
            ) : (
                <ul
                    ref={listRef}
                    onScroll={handleScroll}
                    className='max-h-[675px] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800'
                >
                    {sorted.map((entry, index) => {
                        const versionNumber = entry.version;
                        const versionId = entry._id || `v-${versionNumber ?? index}`;

                        return (
                            <li key={versionId} className='px-4 py-3'>
                                <div className='flex items-start justify-between gap-3'>
                                    <div className='min-w-0 space-y-1'>
                                        <p className='text-sm font-medium text-gray-900 dark:text-white'>
                                            {versionNumber != null ? `v${versionNumber}` : 'Version'}
                                        </p>
                                        <VersionEntryDetails entry={entry} />
                                    </div>
                                    {versionNumber != null && (
                                        <div className='flex flex-col gap-1 shrink-0'>
                                            {onViewVersion && (
                                                <button
                                                    type='button'
                                                    onClick={() => onViewVersion(Number(versionNumber))}
                                                    className='text-xs font-medium text-primary dark:text-primary-darkMode hover:underline'
                                                >
                                                    View
                                                </button>
                                            )}
                                            {canRestore && onRestore && (
                                                <button
                                                    type='button'
                                                    disabled={restoringVersion === versionNumber}
                                                    onClick={() => onRestore(Number(versionNumber))}
                                                    className='text-xs font-medium text-primary dark:text-primary-darkMode hover:underline disabled:opacity-60'
                                                >
                                                    {restoringVersion === versionNumber ? 'Restoring...' : 'Restore'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                    {loadingMore && (
                        <li className='px-4 py-3 text-center text-xs text-gray-500 dark:text-gray-400'>
                            Loading more...
                        </li>
                    )}
                    {!loadingMore && !hasMore && sorted.length > 0 && (
                        <li className='px-4 py-3 text-center text-xs text-gray-400'>
                            All versions loaded
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
};

export default VersionHistory;
